import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import AvatarStack from './AvatarStack';
import { HOME_REDESIGN_IMAGES } from './constants';
import heroCtaArrowUrl from './assets/hero-cta-arrow.svg';
import HomeRedesignFloatingCtaBar from './HomeRedesignFloatingCtaBar';
import StoreDownloadLink from './StoreDownloadLink';
import { useHomeRedesignStrings } from './useHomeRedesignStrings';

const gradHero1 =
  'linear-gradient(135.00000106835844deg, rgb(255, 217, 238) 0%, rgb(255, 217, 238) 2.1757%, rgb(255, 192, 225) 2.1757%, rgb(255, 192, 225) 4.3514%)';
const gradHero2 =
  'linear-gradient(135.0000011525944deg, rgb(244, 233, 218) 0%, rgb(244, 233, 218) 3.1427%, rgb(234, 219, 196) 3.1427%, rgb(234, 219, 196) 6.2854%)';
const gradHero3 =
  'linear-gradient(135deg, rgb(255, 214, 184) 0%, rgb(255, 214, 184) 3.8569%, rgb(255, 192, 151) 3.8569%, rgb(255, 192, 151) 7.7139%)';

const badgeMono =
  'font-jetbrains text-[10px] uppercase leading-normal tracking-[0.36px] text-[rgba(74,33,71,0.6)]';

function PhotoFrame({
  gradient,
  imageSrc,
  badge,
  badgePosition,
}: {
  gradient: string;
  imageSrc: string;
  badge: string;
  badgePosition: 'bl' | 'br';
}) {
  const badgeCls =
    badgePosition === 'bl'
      ? 'absolute bottom-3 left-3'
      : 'absolute bottom-[7.81px] right-[10.45px]';

  return (
    <>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-2xl"
        style={{ backgroundImage: gradient }}
      />
      <img
        alt=""
        src={imageSrc}
        className="absolute inset-0 size-full max-w-none rounded-2xl object-cover"
      />
      <div
        className={`${badgeCls} z-[1] flex min-h-[20px] flex-col justify-center rounded bg-[rgba(255,255,255,0.55)] px-1.5 py-0.5 backdrop-blur-[2px]`}
      >
        <span className={badgeMono}>{badge}</span>
      </div>
    </>
  );
}

/** Matches `screens.hero` in tailwind.config.js */
const HERO_LAYOUT_MIN = 1200;

function heroScales(width: number): { m: number; s: number } {
  if (width < HERO_LAYOUT_MIN) {
    let m: number;
    if (width < 440) {
      m = Math.min(1, Math.max(0.82, (width - 48) / 320));
    } else {
      m = Math.min(1.38, Math.max(0.88, (width - 360) / 400));
    }
    return { m, s: 1 };
  }
  const s = Math.min(1, Math.max(0.72, (width - 720) / 720));
  return { m: 1, s };
}

const HomeRedesignHero: React.FC = () => {
  const [{ m, s }, setScales] = useState(() =>
    typeof window !== 'undefined' ? heroScales(window.innerWidth) : { m: 1, s: 1 },
  );
  const str = useHomeRedesignStrings();
  const heroCtaRowRef = useRef<HTMLDivElement>(null);
  /** True once hero CTAs have been in the viewport (user has "reached" them). */
  const heroCtaWasVisibleRef = useRef(false);
  const [showFloatingCta, setShowFloatingCta] = useState(false);

  useEffect(() => {
    const row = heroCtaRowRef.current;
    if (!row || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          heroCtaWasVisibleRef.current = true;
          setShowFloatingCta(false);
          return;
        }
        if (heroCtaWasVisibleRef.current) {
          setShowFloatingCta(true);
        }
      },
      { root: null, rootMargin: '0px', threshold: 0 },
    );
    observer.observe(row);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const onResize = () => setScales(heroScales(window.innerWidth));
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <section
      className="overflow-visible bg-[#fcf7fa]"
      style={
        {
          '--hero-m': String(m),
          '--hero-s': String(s),
        } as React.CSSProperties
      }
    >
      <div className="mx-auto flex w-full max-w-[1440px] flex-col gap-[clamp(1.5rem,3vw,2rem)] px-[clamp(1rem,4vw,1.5rem)] pb-8 pt-12 max-hero:md:gap-10 max-hero:lg:gap-12 max-hero:md:px-10 max-hero:lg:px-12 hero:flex-row hero:items-start hero:gap-[clamp(2rem,4vw,4rem)] hero:px-[clamp(1.5rem,5vw,5rem)] hero:pb-16 hero:pt-16">
        <div className="flex min-w-0 flex-1 flex-col gap-[clamp(1.5rem,3vw,2rem)] max-hero:md:gap-10 hero:max-w-none hero:gap-[clamp(2rem,3vw,2.5rem)] hero:self-stretch hero:justify-center">
          {/* Headline — single wrapper; all fluid sizes ×1.7 vs previous step */}
          <div className="w-full max-w-xl sm:max-w-2xl md:max-w-4xl hero:max-w-none">
            <h1 className="w-full text-balance font-instrument text-[clamp(5.3125rem,0.595rem+18.36vw,7.65rem)] font-normal not-italic leading-[clamp(4.845rem,0.425rem+16.66vw,6.97rem)] tracking-[clamp(-0.153rem,-0.031rem-0.714vw,-0.201rem)] text-[#2a0f2a] max-hero:md:text-[clamp(5.95rem,1.955rem+11.9vw,8.925rem)] max-hero:md:leading-[clamp(5.355rem,1.7rem+10.88vw,7.905rem)] max-hero:md:tracking-[clamp(-0.17rem,-0.048rem-0.51vw,-0.224rem)] max-hero:lg:text-[clamp(6.5875rem,2.295rem+10.54vw,9.775rem)] max-hero:lg:leading-[clamp(5.865rem,1.955rem+9.52vw,8.67rem)] max-hero:lg:tracking-[clamp(-0.184rem,-0.054rem-0.408vw,-0.247rem)] hero:text-[clamp(4.25rem,1.75rem+7.2vw,9rem)] hero:leading-[clamp(3.72rem,1.55rem+6.35vw,7.92rem)] hero:tracking-[clamp(-0.12rem,-0.05rem-0.28vw,-0.315rem)]">
              <span className="block">
                {str.hero.headlineYou}
                <span className="font-instrument italic text-chisme-primary">{str.hero.headlinePeople}</span>
              </span>
              <span className="block">{str.hero.headlineL2}</span>
              <span className="block">{str.hero.headlineL3}</span>
            </h1>
          </div>

          {/* Subhead — highlight: Figma 3234:13426 (bold, darker, always marked) */}
          <div className="relative w-full max-w-xl shrink-0 sm:max-w-2xl md:max-w-3xl max-hero:md:max-w-[42rem] max-hero:lg:max-w-[48rem] hero:max-w-[min(32.5rem,48vw)]">
            <p className="relative min-w-full font-jakarta text-[clamp(0.9375rem,0.88rem+0.45vw,1rem)] font-normal leading-[clamp(1.35rem,1.2rem+0.65vw,1.5rem)] text-[#5a3158] max-hero:md:text-[clamp(1.0625rem,0.95rem+0.55vw,1.1875rem)] max-hero:md:leading-[clamp(1.5rem,1.3rem+0.75vw,1.75rem)] max-hero:lg:text-[clamp(1.125rem,1rem+0.5vw,1.3125rem)] max-hero:lg:leading-[clamp(1.6rem,1.4rem+0.65vw,1.9rem)] hero:text-[clamp(1rem,0.92rem+0.35vw,1.375rem)] hero:leading-[clamp(1.4rem,1.25rem+0.5vw,1.925rem)]">
              {str.hero.subBefore.trimEnd()}
              <br aria-hidden />
              <span className="font-bold text-[#1a081a] [box-decoration-break:clone] rounded-[0.2em] bg-[#ffe48a] px-[0.12em] py-[0.06em]">
                {str.hero.subHighlight}
              </span>
              {str.hero.subAfter}
            </p>
          </div>

          {/* Mobile collage — design 360×320, scales with --hero-m */}
          <div
            className="relative w-full shrink-0 overflow-visible hero:hidden"
            style={{ height: 'calc(320px * var(--hero-m) + clamp(16px, 4vw, 28px))' }}
          >
            <div className="absolute left-0 top-0 h-[320px] w-[360px] max-w-none origin-top-left will-change-transform [transform:scale(var(--hero-m))]">
            <div className="absolute left-[-6.66px] top-[-5.32px] flex h-[270.634px] w-[223.32px] items-center justify-center">
              <div className="flex-none -rotate-3">
                <div className="relative h-[260px] w-[210px] overflow-hidden rounded-2xl shadow-[0px_12px_30px_0px_rgba(74,33,71,0.1)]">
                  <PhotoFrame
                    gradient={gradHero1}
                    imageSrc={HOME_REDESIGN_IMAGES.hero1}
                    badge={str.hero.badgeBrunch}
                    badgePosition="bl"
                  />
                </div>
              </div>
            </div>
            <div className="absolute left-[189.56px] top-[85px] flex h-[200.001px] w-[162.888px] items-center justify-center">
              <div className="flex-none rotate-[4deg]">
                <div className="relative h-[190px] w-[150px] overflow-hidden rounded-2xl shadow-[0px_12px_30px_0px_rgba(74,33,71,0.1)]">
                  <PhotoFrame
                    gradient="linear-gradient(134.9999995925852deg, rgb(244, 233, 218) 0%, rgb(244, 233, 218) 4.9913%, rgb(234, 219, 196) 4.9913%, rgb(234, 219, 196) 9.9827%)"
                    imageSrc={HOME_REDESIGN_IMAGES.hero2}
                    badge={str.hero.badgeRunClub}
                    badgePosition="bl"
                  />
                </div>
              </div>
            </div>
            <div className="absolute left-[17.26px] top-[263.59px] flex h-[54.083px] w-[233.887px] items-center justify-center">
              <div className="flex-none -rotate-2">
                <div className="flex h-[46px] items-center gap-[10px] rounded-full bg-white px-[14px] py-2.5 shadow-[0px_6px_10px_rgba(74,33,71,0.12)]">
                  <AvatarStack size="sm" letters={['M', 'L', 'A', 'J']} />
                  <div className="w-[114px] font-jakarta text-xs leading-[13.2px] text-[#5a3158]">
                    <p className="font-semibold leading-normal text-[#2a0f2a]">{str.hero.socialLine1}</p>
                    <p>{str.hero.socialLine2}</p>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>

          {/* CTA — mobile 3234:13948 gap 10px / desktop 3234:13431 gap 16px; ref for floating bar (Figma 3234:14134) */}
          <div
            ref={heroCtaRowRef}
            className="flex w-full shrink-0 flex-col gap-[10px] max-hero:md:flex-row max-hero:md:flex-wrap max-hero:md:items-center max-hero:md:gap-4 max-hero:lg:gap-5 hero:flex-row hero:items-center hero:gap-4"
          >
            <StoreDownloadLink
              popoverAlign="center"
              tabIndex={0}
              className="inline-flex h-[clamp(2.75rem,2.4rem+1.1vw,3.75rem)] w-full items-center justify-center gap-[10px] rounded-full bg-chisme-primary px-[26px] font-jakarta text-[clamp(0.9375rem,0.88rem+0.35vw,1.0625rem)] font-bold leading-normal tracking-[-0.01em] text-white max-hero:md:h-14 max-hero:md:w-auto max-hero:md:min-w-[12.5rem] max-hero:md:px-8 max-hero:md:text-[1.05rem] max-hero:lg:h-[3.75rem] max-hero:lg:text-[1.0625rem] hero:h-[60px] hero:w-auto hero:px-8 hero:text-[17px]"
            >
              <span>{str.hero.ctaPrimary}</span>
              <img
                src={heroCtaArrowUrl}
                alt=""
                className="size-[clamp(1rem,0.92rem+0.35vw,1.125rem)] shrink-0 max-hero:md:size-[1.125rem] hero:size-[18px]"
                aria-hidden
              />
            </StoreDownloadLink>
            <Link
              to="/ciudades"
              className="inline-flex h-[clamp(2.75rem,2.4rem+1.1vw,3.75rem)] w-full items-center justify-center rounded-full border-2 border-chisme-primary py-0.5 font-jakarta text-[clamp(0.875rem,0.82rem+0.35vw,0.9375rem)] font-semibold leading-normal text-chisme-primary max-hero:md:h-14 max-hero:md:w-auto max-hero:md:min-w-[12.5rem] max-hero:md:px-7 max-hero:md:text-[0.98rem] max-hero:lg:h-[3.75rem] max-hero:lg:text-[1rem] hero:h-[60px] hero:w-auto hero:px-6 hero:text-[15px]"
            >
              <span className="p-2.5">{str.hero.ctaSecondary}</span>
            </Link>
          </div>
        </div>

        {/* Desktop collage — 3234:13455, scales with --hero-s */}
        <div
          className="relative hidden min-h-0 min-w-[min(100%,300px)] flex-[1.05] basis-0 overflow-visible hero:block"
          style={{
            minHeight: 'calc(48px + 660px * var(--hero-s))',
          }}
        >
          <div className="absolute right-0 top-3 h-[660px] w-[640px] max-w-none origin-top-right will-change-transform [transform:scale(var(--hero-s))]">
          <div className="absolute left-[-11.28px] top-[-8.6px] flex h-[457.191px] w-[362.562px] items-center justify-center">
            <div className="flex-none -rotate-3">
              <div className="relative h-[440px] w-[340px] overflow-hidden rounded-2xl shadow-[0px_24px_60px_0px_rgba(74,33,71,0.14)]">
                <PhotoFrame
                  gradient={gradHero1}
                  imageSrc={HOME_REDESIGN_IMAGES.hero1}
                  badge={str.hero.badgeBrunch}
                  badgePosition="bl"
                />
              </div>
            </div>
          </div>
          <div className="absolute left-[346.89px] top-[71.99px] flex h-[316.011px] w-[260.342px] items-center justify-center">
            <div className="flex-none rotate-[4deg]">
              <div className="relative h-[300px] w-[240px] overflow-hidden rounded-2xl shadow-[0px_24px_60px_0px_rgba(74,33,71,0.14)]">
                <PhotoFrame
                  gradient={gradHero2}
                  imageSrc={HOME_REDESIGN_IMAGES.hero2}
                  badge={str.hero.badgePottery}
                  badgePosition="bl"
                />
              </div>
            </div>
          </div>
          <div className="absolute left-[312.94px] top-[386.58px] flex h-[246.834px] w-[208.254px] items-center justify-center">
            <div className="flex-none -rotate-2">
              <div className="relative h-[240px] w-[200px] overflow-hidden rounded-2xl shadow-[0px_24px_60px_0px_rgba(74,33,71,0.14)]">
                <PhotoFrame
                  gradient={gradHero3}
                  imageSrc={HOME_REDESIGN_IMAGES.hero3}
                  badge={str.hero.badgeRunClub}
                  badgePosition="br"
                />
              </div>
            </div>
          </div>
          <div className="absolute left-[19px] top-[570.75px] flex h-[75.785px] w-[340.825px] items-center justify-center">
            <div className="flex-none -rotate-2">
              <div className="flex h-16 max-w-[338.798px] items-center gap-[13.998px] rounded-full bg-white px-5 py-3.5 shadow-[0px_14px_20px_rgba(74,33,71,0.18)]">
                <AvatarStack size="lg" />
                <div className="w-[144.805px] font-jakarta">
                  <p className="text-[15px] font-bold leading-[16.5px] text-[#2a0f2a]">{str.hero.socialLine1}</p>
                  <p className="text-xs leading-[13.2px] text-[#5a3158]">{str.hero.socialLine2}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute left-[460.35px] top-[12.02px] flex h-[48.953px] w-[118.019px] items-center justify-center">
            <div className="flex-none rotate-[4deg]">
              <div className="flex h-[41px] flex-col items-start rounded-full bg-white px-3.5 py-1.5 shadow-[0px_6px_8px_rgba(74,33,71,0.1)]">
                <p className="whitespace-nowrap font-instrument text-[22px] italic leading-normal text-chisme-primary">
                  {str.hero.finally}
                </p>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* Mobile floating CTA — Figma 3234:14134; slides up from bottom when hero CTAs leave view */}
      <div
        className={`pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center overflow-visible px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-1 hero:hidden motion-safe:transition-transform motion-safe:duration-500 motion-safe:ease-out motion-reduce:transition-none ${
          showFloatingCta
            ? 'pointer-events-auto translate-y-0'
            : 'translate-y-[calc(100%+1.5rem)]'
        }`}
        aria-hidden={!showFloatingCta}
      >
        <HomeRedesignFloatingCtaBar linkTabIndex={showFloatingCta ? 0 : -1} />
      </div>
    </section>
  );
};

export default HomeRedesignHero;
